import { MainApi } from 'api/MainApi'
import Loader from 'components/Loader/Loader'
import { useEffectOnce } from 'hooks/useEffectOnce'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useEffect, useRef, useState } from 'react'
import { PROGRESS_TYPES } from 'utils/constants'
import { IRobotMap } from 'utils/interfaces'
import { optionalIntParam, pageParam, stringParam } from 'utils/urlQueryState'

import RobotMapListView from './RobotMapListView/RobotMapListView'

const RobotMapList = () => {
  const [url, setUrl] = useUrlQueryState({
    q: stringParam(),
    page: pageParam(),
    facility: optionalIntParam(),
    map: optionalIntParam(),
  })
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [robotMaps, setRobotMaps] = useState<IRobotMap[]>([])
  const currentPage = url.page
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const searchQuery = url.q
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(url.q)
  const facilityId = url.facility
  const skipPageResetRef = useRef(true)

  const fetchData = async (
    page: number = currentPage,
    search: string = debouncedSearchQuery,
    fId: number | undefined = facilityId,
    pSize: number = pageSize
  ) => {
    try {
      const response = await MainApi.fetchRobotMaps(page, pSize, search || undefined, fId)
      setRobotMaps(response.results)
      setTotalCount(response.count)
    } catch (error) {
      setLoadingProgress(PROGRESS_TYPES.ERROR)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    const someIsProcessing = robotMaps.some(map => map.is_processing)
    if (someIsProcessing) {
      const interval = setInterval(() => {
        fetchData()
      }, 2000)
      return () => clearInterval(interval)
    }
  }, [robotMaps])

  useEffect(() => {
    if (loadingProgress === PROGRESS_TYPES.SUCCESS) {
      if (!skipPageResetRef.current && currentPage !== 1) {
        setUrl({ page: 1 })
        fetchData(1, debouncedSearchQuery, facilityId, pageSize)
        return
      }
      fetchData(currentPage, debouncedSearchQuery, facilityId, pageSize)
    }
  }, [debouncedSearchQuery, facilityId, pageSize])

  useEffect(() => {
    skipPageResetRef.current = false
  }, [])

  useEffectOnce(() => {
    const initialize = async () => {
      setLoadingProgress(PROGRESS_TYPES.WORK)
      await fetchData()
      setLoadingProgress(PROGRESS_TYPES.SUCCESS)
    }
    initialize()
  })

  const onPageChanged = (page: number) => {
    setUrl({ page })
    fetchData(page)
  }

  const onSearchChanged = (search: string) => {
    setUrl({ q: search })
  }

  const onFacilityChanged = (id: number | undefined) => {
    setUrl({ facility: id, page: 1 })
  }

  const stateList = {
    [`${PROGRESS_TYPES.SUCCESS}`]: (
      <RobotMapListView
        robotMaps={robotMaps}
        onMapsChanged={() => fetchData()}
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChanged={onPageChanged}
        onPageSizeChange={setPageSize}
        searchQuery={searchQuery}
        onSearchChanged={onSearchChanged}
        facilityId={facilityId}
        onFacilityChanged={onFacilityChanged}
        selectedMapId={url.map}
        onSelectedMapIdChange={id => setUrl({ map: id })}
      />
    ),
    [`${PROGRESS_TYPES.WORK}`]: <Loader />,
    [`${PROGRESS_TYPES.ERROR}`]: <div>Error loading maps</div>,
    [`${PROGRESS_TYPES.IDLE}`]: null,
  }

  return stateList[loadingProgress] || null
}

export default RobotMapList
